import { useMemo } from 'react';

import { type Permission } from '@irl/types';

import {
  type ChatAccessContext,
  checkPermission,
  hasPermissionInAnyChat,
  hasPermissionInChats,
} from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth';

function toChatAccess(
  chats: ReturnType<typeof useAuthStore.getState>['chats'],
): ChatAccessContext[] {
  return chats.map((chat) => ({
    chatId: chat.id,
    role: chat.role,
    customPermissions: chat.permissions,
  }));
}

export function useHasPermission(permission: Permission, chatId?: string): boolean {
  const isGlobalOperator = useAuthStore((s) => s.isGlobalOperator);
  const chats = useAuthStore((s) => s.chats);
  const isAuthReady = useAuthStore((s) => s.isAuthReady);

  return useMemo(() => {
    if (!isAuthReady) {
      return false;
    }
    const accessChats = toChatAccess(chats);
    if (chatId) {
      const chat = accessChats.find((item) => item.chatId === chatId);
      return checkPermission(isGlobalOperator, chat, permission);
    }
    return hasPermissionInAnyChat(isGlobalOperator, accessChats, permission);
  }, [chatId, chats, isAuthReady, isGlobalOperator, permission]);
}

export function useHasPermissionInChats(permission: Permission, chatIds: string[]): boolean {
  const isGlobalOperator = useAuthStore((s) => s.isGlobalOperator);
  const chats = useAuthStore((s) => s.chats);
  const isAuthReady = useAuthStore((s) => s.isAuthReady);

  return useMemo(() => {
    if (!isAuthReady) {
      return false;
    }
    return hasPermissionInChats(isGlobalOperator, toChatAccess(chats), chatIds, permission);
  }, [chatIds, chats, isAuthReady, isGlobalOperator, permission]);
}

export function usePermissionState(
  permission: Permission,
  chatId?: string,
): { allowed: boolean; isLoading: boolean } {
  const isAuthReady = useAuthStore((s) => s.isAuthReady);
  const allowed = useHasPermission(permission, chatId);

  return {
    allowed,
    isLoading: !isAuthReady,
  };
}
